import { appointmentAvailabilityPeriodCalculation } from "@accurx/self-book";
import {
    ComposeStateType,
    ContactDetail,
    MessageEditor,
} from "domains/message-component/reducer.types";
import { calculateCharacterCount } from "domains/message-component/utils";

import { OnMessageSendFnComposeData } from "../../types/Compose.types";

const getSelfBookLinkOptions = ({
    message,
}: {
    message: MessageEditor;
}): OnMessageSendFnComposeData["selfBookLink"] => {
    const currentDate = new Date();
    const sendTime =
        message.sendAt === null ? currentDate : message.sendAt.sendAtDateTime;
    const sendTimeAsDateTime = new Date(sendTime);

    const calculatedFutureDate = appointmentAvailabilityPeriodCalculation(
        sendTimeAsDateTime,
        message.selfBookLink?.appointmentAvailabilityPeriod,
    );

    const selfBookOptions = message.selfBookLink
        ? {
              ...message.selfBookLink,
              availabilityRestriction: {
                  from: sendTimeAsDateTime.toISOString(),
                  to: calculatedFutureDate.toISOString(),
              },
          }
        : null;

    return selfBookOptions;
};

const getTotalCounts = ({
    state,
    workspaceName,
    hasSelfbookLink,
}: {
    state: ComposeStateType;
    workspaceName: string;
    hasSelfbookLink: boolean;
}) => {
    // Sum up the fragment counts and check if any of the messages contain unicode characters
    // Character count is not relevant for multiple messages
    const totalCounts = state.messages.reduce(
        (prev, message) => {
            const { characterCount, fragmentCount, isUnicode } =
                calculateCharacterCount({
                    body: message.messageBody,
                    footer: message.messageSignature,
                    workspaceName: workspaceName,
                    patientResponseEnabled: message.isPatientResponseEnabled,
                    // Do not count attachments that are not successfully uploaded
                    attachmentsCount: message.attachments.filter(
                        (attachment) => attachment.status === "success",
                    ).length,
                    hasQuestionnaire:
                        state.template.type === "QuestionnaireTemplate",
                    nhsAdviceLink: message.nhsAdviceLink,
                    hasSelfbookLink,
                });

            return {
                characterCount: prev.characterCount + characterCount,
                fragmentCount: prev.fragmentCount + fragmentCount,
                isUnicode: prev.isUnicode || isUnicode,
            };
        },
        {
            characterCount: 0,
            fragmentCount: 0,
            isUnicode: false,
        },
    );

    return totalCounts;
};

export const prepareSendMessage = ({
    state,
    workspaceName,
}: {
    state: ComposeStateType;
    workspaceName: string;
}): OnMessageSendFnComposeData => {
    // Only single messages can have self book links
    const canSendSelfBookLink = state.messages.length === 1;

    const totalCounts = getTotalCounts({
        state,
        workspaceName,
        hasSelfbookLink:
            canSendSelfBookLink && state.messages[0].selfBookLink !== null,
    });

    return {
        characterCount:
            state.messages.length === 1
                ? totalCounts.characterCount
                : undefined,
        fragmentCount: totalCounts.fragmentCount,
        isUnicode: totalCounts.isUnicode,
        messages: state.messages.map((message) => {
            return {
                attachments: message.attachments.filter(
                    ({ status }) => status === "success",
                ),
                isPatientResponseEnabled: message.isPatientResponseEnabled,
                messageBody: message.messageBody,
                messageSignature: message.messageSignature,
                nhsAdviceLink: message.nhsAdviceLink,
                sendAt: message.sendAt,
            };
        }),
        contactDetails: state.contactDetails as ContactDetail,
        template: state.template,
        isSaveToRecordEnabled: state.isSaveToRecordEnabled,
        selfBookLink: canSendSelfBookLink
            ? getSelfBookLinkOptions({ message: state.messages[0] })
            : null,
    };
};
